/**
 * PATH: apps/hr/src/pages/payroll/PayrollPage.tsx
 * PURPOSE: Payroll management with pay runs and payslips
 * 
 * ROLE IN ARCHITECTURE: Payroll processing and history view
 * 
 * NOTES FOR FUTURE AI:
 * - Pay runs stay empty until a payroll API exists. Do not restore dummy totals.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DollarSign,
  Plus,
  Search,
  ChevronRight,
} from 'lucide-react'

import { cn, formatDate, formatCurrency } from '../../lib/utils'
import type { PayRun, PayRunStatus } from '../../types'

const CONNECTED_PAY_RUNS: PayRun[] = []

// =============================================================================
// PAY RUN TABLE
// =============================================================================

function PayRunTable({ payRuns, onViewRun }: { payRuns: PayRun[]; onViewRun: (id: string) => void }) {
  const statusStyles: Record<PayRunStatus, string> = {
    draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    in_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    processed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  }

  const statusLabels: Record<PayRunStatus, string> = {
    draft: 'Draft',
    in_review: 'In Review',
    approved: 'Approved',
    processed: 'Processed',
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Pay Period</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Pay Date</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Employees</th>
            <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Gross</th>
            <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Net</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {payRuns.map((run) => (
            <tr 
              key={run.id} 
              className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
              onClick={() => onViewRun(run.id)}
            >
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {formatDate(run.periodStart, { day: 'numeric', month: 'short' })} - {formatDate(run.periodEnd, { day: 'numeric', month: 'short' })}
                    </p>
                    {run.notes && <p className="text-xs text-slate-500">{run.notes}</p>}
                  </div>
                </div>
              </td>
              <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                {formatDate(run.payDate, { day: 'numeric', month: 'short', year: 'numeric' })}
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">{run.employeeCount}</span>
                </div>
              </td>
              <td className="py-3 px-4 text-sm text-slate-900 dark:text-white text-right font-medium tabular-nums">
                {formatCurrency(run.totalGross)}
              </td>
              <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                {formatCurrency(run.totalNet)}
              </td>
              <td className="py-3 px-4">
                <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium', statusStyles[run.status])}>
                  {statusLabels[run.status]}
                </span>
              </td>
              <td className="py-3 px-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onViewRun(run.id)
                  }}
                  className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {payRuns.length === 0 && (
        <div className="py-12 text-center">
          <DollarSign className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" />
          <p className="text-slate-500 dark:text-slate-400">No pay runs are connected.</p>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function PayrollPage() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<'all' | PayRunStatus>('all')
  const [search, setSearch] = useState('')
  const payRuns = CONNECTED_PAY_RUNS

  const filteredPayRuns = useMemo(() => {
    let data = [...payRuns]
    if (statusFilter !== 'all') {
      data = data.filter(r => r.status === statusFilter)
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      data = data.filter(r => 
        r.notes?.toLowerCase().includes(q) || 
        r.createdByName?.toLowerCase().includes(q)
      )
    }
    return data
  }, [payRuns, statusFilter, search])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Payroll</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            No pay runs are connected.
          </p>
        </div>
        <button
          type="button"
          disabled
          title="Payroll is not connected"
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium opacity-60"
        >
          <Plus className="w-4 h-4" />
          New Pay Run
        </button>
      </div>

      {/* Pay Runs Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
          <h3 className="font-semibold text-slate-900 dark:text-white">Pay Run History</h3>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 w-48 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              {(['all', 'in_review', 'approved', 'processed'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-colors capitalize',
                    statusFilter === f
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                  )}
                >
                  {f === 'in_review' ? 'Review' : f}
                </button>
              ))}
            </div>
          </div>
        </div>
        <PayRunTable 
          payRuns={filteredPayRuns} 
          onViewRun={(id) => navigate(`/payroll/${id}`)} 
        />
      </div>
    </div>
  )
}
